import { useState } from "react";
import { api } from "../api";
import { ApiError, type WorkspaceSummary } from "../api/types";
import { Button, SelectField, TextField } from "../compose/controls";
import { Dialog } from "../compose/Dialog";
import { LayoutGrid } from "../compose/Icon";
import { ACCENTS, DEFAULT_ACCENT } from "../lib/accents";
import { isValidSlug, slugify } from "../lib/slug";

/**
 * Create workspace — a Cerebr8 STAFF action (design screens §9 staff console:
 * "[ + Create workspace ]"). Calls POST /api/workspaces, which also seeds the
 * folder skeleton and enrols the creating staff. On success the caller navigates
 * into the new room. Deny-by-default: the button that opens this is only rendered
 * for staff, and the server rejects a non-staff caller regardless.
 */

/** The accent picker options — the 8-hue archival set (lib/accents). */
const ACCENT_OPTIONS = Object.keys(ACCENTS).map((name) => ({
  value: name,
  label: name
    .split("-")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" "),
}));

/** Slug derivation + validation now live in `lib/slug` — ONE implementation
 *  shared with `<NewArticleDialog>` (the article-unit slug obeys the same server
 *  contract). Re-exported so existing importers (and `api/workspaceMgmt.test.ts`)
 *  keep working unchanged. */
export { isValidSlug, slugify };

export function CreateWorkspaceDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (workspace: WorkspaceSummary) => void;
}) {
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [client, setClient] = useState("");
  const [accent, setAccent] = useState(DEFAULT_ACCENT);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // The slug auto-follows the name until the user edits it directly.
  const effectiveSlug = slugTouched ? slug : slugify(name);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!name.trim()) {
      setError("Give the workspace a name.");
      return;
    }
    if (!isValidSlug(effectiveSlug)) {
      setError("Slug must be lowercase letters, numbers and hyphens.");
      return;
    }
    setBusy(true);
    try {
      const ws = await api.createWorkspace({
        slug: effectiveSlug,
        name: name.trim(),
        client: client.trim() || undefined,
        accent,
      });
      onCreated(ws);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setError("That slug is already taken — choose another.");
      } else if (err instanceof ApiError && err.status === 403) {
        setError("Only Cerebr8 staff can create a workspace.");
      } else {
        setError(
          err instanceof ApiError ? err.message : "Could not create the workspace.",
        );
      }
      setBusy(false);
    }
  }

  return (
    <Dialog
      title="New workspace"
      subtitle="Create a room and seed its folder skeleton."
      onClose={onClose}
      testid="create-workspace-dialog"
    >
      <form onSubmit={submit}>
        {error ? (
          <div className="auth__error" role="alert">
            {error}
          </div>
        ) : null}
        <TextField
          label="Name"
          placeholder="Acme Casino"
          value={name}
          data-testid="cw-name"
          onChange={(e) => setName(e.target.value)}
          autoFocus
        />
        <TextField
          label="Slug"
          hint="Used in the URL — lowercase letters, numbers and hyphens."
          placeholder="acme-casino"
          value={effectiveSlug}
          data-testid="cw-slug"
          onChange={(e) => {
            setSlugTouched(true);
            setSlug(e.target.value);
          }}
        />
        <TextField
          label="Client"
          placeholder="Acme GmbH"
          value={client}
          data-testid="cw-client"
          onChange={(e) => setClient(e.target.value)}
        />
        <SelectField
          label="Accent"
          options={ACCENT_OPTIONS}
          value={accent}
          data-testid="cw-accent"
          onChange={(e) => setAccent(e.target.value)}
        />
        <div className="dialog__actions">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={busy}
            data-testid="cw-submit"
            icon={<LayoutGrid size={15} strokeWidth={1.5} />}
          >
            {busy ? "Creating…" : "Create workspace"}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
